import { Button } from "@/components/ui/button";
import { Note } from "@/components/ui/Note";
import { Panel } from "@/components/ui/Panel";
import {
  useInventionElements,
  useRerunExtraction,
} from "@/hooks/use-invention";
import { refusalDetail } from "@/services/api-client";

const NOT_READ_AGAIN = "Try again.";

/**
 * The elements were read from other intake files than the matter holds now.
 *
 * Before the attorney first confirms them, Re-run reads them again, keeping what they
 * confirmed, edited or added. After that, positions name the elements, so they are not read
 * again: the changes from new files come as suggested changes, and the banner says so with
 * no button. The server decides both (`sources_changed`, `rederivable`); this only says it.
 */
export function SourcesChanged({
  taskId,
  className,
}: {
  taskId: string | null;
  className?: string;
}) {
  const { data } = useInventionElements(taskId);
  const rerun = useRerunExtraction(taskId);
  if (data?.sources_changed !== true) return null;
  return (
    <Panel
      shape="card"
      pad="snug"
      tone="warning"
      className={className ?? "mt-4 text-[13px]"}
    >
      {data.rederivable ? (
        <>
          <strong>
            Your source documents changed after the elements were read.
          </strong>{" "}
          Read them again to update the elements. What you confirmed, edited or
          added stays.{" "}
          <Button
            variant="link"
            disabled={rerun.isPending}
            onClick={() => {
              rerun.mutate();
            }}
          >
            {rerun.isPending ? "Starting…" : "Re-run"}
          </Button>
          {rerun.error !== null && (
            <Note tone="destructive" role="alert">
              The elements could not be read again.{" "}
              {refusalDetail(rerun.error, NOT_READ_AGAIN)}
            </Note>
          )}
        </>
      ) : (
        <>
          <strong>
            Your source documents changed after you confirmed the elements.
          </strong>{" "}
          Patent Studio does not read the elements again. It suggests the
          changes from new files for you to accept or reject.
        </>
      )}
    </Panel>
  );
}
